// 라군의 하루 10분: 낮(0~0.38) → 천천히 노을과 밤(0.38~0.56) → 밤(0.56~0.84) → 새벽(0.84~1).
// 하늘·안개·배경색, 공용 조명, 물·가로등·창문·용암 발광, 밤 조명·후광, 별·오로라, 폭포·헹굼 폭포 셰이더, 빙하·오로라 구름 소품. 프로토타입 2659–2733.
import * as THREE from 'three';
import { LEGACY, type Lights } from '../../engine/lights';
import type { Stage } from '../../engine/renderer';
import { AUR_CLOUD_MAT, GLACIER_MAT, ICE_MAT } from '../../items/head';
import { WATER_Y } from '../quiet/baths';
import { DAYLEN } from './layout';
import { LAMPS, LAVAGLOW, LWATER, LWIN, type LagoonScene } from './scene';

const DAY = { top: new THREE.Color('#8FB8DB'), hor: new THREE.Color('#DCE6EE'), fog: new THREE.Color('#D8E3EB') };
const DUSK = { top: new THREE.Color('#5E5A8E'), hor: new THREE.Color('#F2A98A') };
const NIGHT = { top: new THREE.Color('#040A1A'), hor: new THREE.Color('#10203A'), fog: new THREE.Color('#122236') };
const cA = new THREE.Color(), cB = new THREE.Color(), teal = new THREE.Color(0x2BB5B8), aurGreen = new THREE.Color(0x2BD27A);

/** 0 = 낮, 1 = 밤. 노을·새벽은 부드럽게. */
export function nightAt(ph: number): number {
  const sm = (x: number) => x * x * (3 - 2 * x);
  if (ph < 0.38) return 0;
  if (ph < 0.56) return sm((ph - 0.38) / 0.18);
  if (ph < 0.84) return 1;
  return 1 - sm((ph - 0.84) / 0.16);
}

export interface DayNight {
  /** 라군 안의 시각 (초). 하루의 4분의 1(아침)에서 시작한다. */
  time: number;
  phase(): number;
  /** ph까지 10초쯤에 걸쳐 시간을 빨리 감는다 (lab 확인용). */
  seek(ph: number): void;
  /** 헹굼 폭포 아래서 씻는 카피바라 (물보라가 머리 위에서 튄다). */
  rinseUser: { c: { root: THREE.Object3D }; size: number; washT?: number } | null;
  tick(t: number, dt: number): void;
  /** 라군을 떠날 때 공용 조명·안개·배경색을 낮 값으로 되돌린다. */
  reset(): void;
}

export interface DayNightHooks {
  /** 오로라가 한창일 때 한 번 (카피바라 반응, 안내 문구). */
  onAurora?(): void;
}

export function createDayNight(ls: LagoonScene, stage: Stage, lights: Lights, hooks: DayNightHooks = {}): DayNight {
  let seekPh: number | null = null, aurSaid = false;
  const D: DayNight = {
    time: 0.25 * DAYLEN, rinseUser: null,
    phase: () => (D.time % DAYLEN) / DAYLEN,
    seek(ph) { seekPh = ph; },
    tick(t, dt) {
      if (seekPh !== null) {
        const left = (((seekPh - D.phase()) % 1) + 1) % 1 * DAYLEN;
        if (left < 0.5) { seekPh = null; D.time += left; }
        else D.time += Math.min(left, dt * (1 + Math.min(left, 140) * 0.25));
      } else D.time += dt;
      const ph = D.phase(), n = nightAt(ph);
      const dusk = Math.max(0, 1 - Math.abs(ph - 0.47) / 0.09) + Math.max(0, 1 - Math.abs(ph - 0.92) / 0.08);
      cA.copy(DAY.top).lerp(NIGHT.top, n).lerp(DUSK.top, dusk * 0.6); (ls.skyMat.uniforms.top!.value as THREE.Color).copy(cA);
      cB.copy(DAY.hor).lerp(NIGHT.hor, n).lerp(DUSK.hor, dusk * 0.75); (ls.skyMat.uniforms.hor!.value as THREE.Color).copy(cB);
      cA.copy(DAY.fog).lerp(NIGHT.fog, n).lerp(DUSK.hor, dusk * 0.3); stage.fog?.color.copy(cA); stage.renderer.setClearColor(cA, 1);
      const sv = 1 - 0.6 * n;
      ls.steam.forEach((sp) => { if (!sp.userData.keepColor) sp.material.color.setRGB(sv * 0.95, sv, sv * 1.05); });
      lights.hemi.intensity = (0.55 - 0.25 * n) * LEGACY; lights.key.intensity = (0.58 - 0.4 * n) * LEGACY; lights.key.color.setRGB(1 - 0.35 * n, 0.95 - 0.15 * n, 0.87 + 0.13 * n);
      const aur = Math.pow(Math.max(0, (n - 0.5) / 0.5), 1.5);
      LWATER.emissive.copy(teal).lerp(aurGreen, aur * 0.35); LWATER.emissiveIntensity = 0.85 * n;
      GLACIER_MAT.emissiveIntensity = 0.22 + 0.45 * n; ICE_MAT.emissiveIntensity = 0.22 + 0.5 * n;
      AUR_CLOUD_MAT.emissive.setHSL(0.4 + 0.18 * (0.5 + 0.5 * Math.sin(t * 0.35)), 0.85 * n, 0.5 + 0.5 * (1 - n)); AUR_CLOUD_MAT.emissiveIntensity = 0.3 + 0.65 * n;
      LAMPS.emissiveIntensity = 0.35 + 1.4 * n; LWIN.emissiveIntensity = 0.12 + 1.0 * n; LAVAGLOW.emissiveIntensity = 0.6 + 1.6 * n;
      ls.nightLights.forEach((l) => { l.intensity = 0.9 * n * LEGACY; });
      ls.halos.forEach((h) => { h.material.opacity = 0.75 * n; });
      ls.auroraU.uT.value = t; ls.auroraU.uA.value = aur * 1.25; ls.starsMat.opacity = n * 0.95;
      ls.fallMat.uniforms.uT!.value = t; ls.fallMat.uniforms.uN!.value = n;
      ls.rinseMat.uniforms.uT!.value = t;
      const R = ls.rinse;
      if (R) {
        const u = D.rinseUser && D.rinseUser.washT ? D.rinseUser : null;
        R.splash.forEach((sp, i) => {
          const q = (t * 0.9 + i / R.splash.length) % 1, an = i * 2.4 + t * 0.3;
          const hy = u ? u.c.root.position.y + 2.3 * (u.size || 1) : WATER_Y + 0.1;
          sp.position.set(R.spot.x + Math.cos(an) * (0.25 + q * 0.7), hy + q * 0.35, R.spot.z + Math.sin(an) * (0.25 + q * 0.7));
          const sc = (u ? 0.9 : 0.7) + q * 1.1; sp.scale.set(sc, sc, 1); sp.material.opacity = Math.sin(q * Math.PI) * (u ? 0.75 : 0.45); sp.material.color.setRGB(1, 1, 1);
        });
      }
      if (aur > 0.6 && !aurSaid) { aurSaid = true; hooks.onAurora?.(); }
      if (aur < 0.1) aurSaid = false;
    },
    reset() {
      lights.hemi.intensity = 0.55 * LEGACY; lights.key.intensity = 0.58 * LEGACY; lights.key.color.set(0xFFF1DE);
      stage.fog?.color.set(0xDCE5EC); stage.renderer.setClearColor(0xDCE5EC, 1);
    },
  };
  return D;
}
